import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Crown, CheckCircle2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { getClientInfo } from '../supabase';

const GabiPro = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const autoActivationTried = useRef(false);

  const loadStatus = async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const client = await getClientInfo(user.email);
      setIsAdmin(Boolean(client?.admin));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const activatePro = async () => {
    if (!user?.email) return;
    setActivating(true);
    setError('');
    setMessage('');
    try {
      const activationWebhook = process.env.REACT_APP_N8N_WEBHOOK_PRO_ACTIVATE;
      if (!activationWebhook) {
        throw new Error('Falta REACT_APP_N8N_WEBHOOK_PRO_ACTIVATE en .env');
      }

      const response = await fetch(activationWebhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: user.email,
          userName: user.displayName || null,
          source: 'gabi_pro_activation',
          activatedAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `Error ${response.status} al activar GABI Pro`);
      }

      await response.json().catch(() => null);
      await loadStatus();
      setMessage('Pago verificado. Tu cuenta se ha actualizado a GABI Pro.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo activar GABI Pro.');
    } finally {
      setActivating(false);
    }
  };

  useEffect(() => {
    const shouldAutoActivate = searchParams.get('paid') === 'true';
    if (!shouldAutoActivate || autoActivationTried.current) return;
    autoActivationTried.current = true;
    void activatePro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user?.email]);

  const handleGoToCheckout = () => {
    setError('');
    const checkoutUrl = process.env.REACT_APP_GABI_PRO_CHECKOUT_URL;
    if (!checkoutUrl) {
      setError('Falta REACT_APP_GABI_PRO_CHECKOUT_URL en .env');
      return;
    }
    window.location.href = checkoutUrl;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-20 pb-8 px-4 min-h-[calc(100vh-4rem)] flex items-center">
        <div className="max-w-3xl mx-auto w-full">
          <div className="glass rounded-3xl border border-primary/20 p-6 sm:p-8 shadow-xl">
            <div className="flex items-start gap-3 mb-5">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-violet-500/10 ring-1 ring-primary/25">
                <Crown className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">GABI Pro</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Desbloquea la experiencia avanzada y accede al dashboard profesional.
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-border/60 bg-background/60 p-4 sm:p-5">
              <p className="text-2xl font-extrabold text-primary mb-3">50€</p>
              <p className="text-sm font-semibold text-foreground mb-3">Qué incluye GABI Pro</p>
              <ul className="space-y-2 text-sm text-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />
                  Dashboard avanzado
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />
                  Vista de datos y acciones mejoradas
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />
                  Flujo premium de informes y gestión
                </li>
              </ul>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Button type="button" variant="hero" onClick={handleGoToCheckout} disabled={loading || activating || isAdmin}>
                {isAdmin ? 'Ya eres Pro' : 'Pasar a GABI Pro (50€)'}
              </Button>
              <Button type="button" variant="outline" onClick={() => void activatePro()} disabled={loading || activating || isAdmin}>
                {activating ? 'Verificando pago...' : 'Ya he pagado, activar Pro'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>
                Ir al dashboard
              </Button>
            </div>

            {loading && <p className="text-sm text-muted-foreground mt-3">Comprobando estado de tu cuenta...</p>}
            {message && <p className="text-sm text-emerald-600 mt-3">{message}</p>}
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            {isAdmin && (
              <p className="text-sm text-primary mt-3 font-medium">
                Tu cuenta ya está en modo Pro.
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default GabiPro;
